/**
 * Render interpolation for the fixed-step simulation.
 *
 * update() runs at a fixed 60 Hz but the screen refreshes at 60/90/120/144 Hz and frames don't
 * line up with steps, so objects moved only in update() (and cameras following them) judder.
 * Register moving roots with `ctx.smooth(obj)`: Game snapshots their transform before every
 * fixed step, renders them at lerp(previous, current, alpha) — including during stage.frame(),
 * so camera rigs follow the smooth position — and restores the true transform afterwards.
 *
 * Call `ctx.teleport(obj)` after moving an object discontinuously (respawn, warp) so it doesn't
 * streak across the screen. Apply purely visual offsets (bob, squash) to CHILD objects, not to
 * the registered root (the root is restored to its simulated transform after each render).
 */
import { Quaternion, Vector3, type Object3D } from 'three';

interface Entry {
  obj: Object3D;
  rotation: boolean;
  prevP: Vector3;
  prevQ: Quaternion;
  curP: Vector3;
  curQ: Quaternion;
  applied: boolean;
}

export class Interpolator {
  private entries = new Map<Object3D, Entry>();

  add(obj: Object3D, opts: { rotation?: boolean } = {}): void {
    if (this.entries.has(obj)) return;
    this.entries.set(obj, {
      obj,
      rotation: opts.rotation ?? false,
      prevP: obj.position.clone(),
      prevQ: obj.quaternion.clone(),
      curP: obj.position.clone(),
      curQ: obj.quaternion.clone(),
      applied: false,
    });
  }

  remove(obj: Object3D): void {
    this.entries.delete(obj);
  }

  /** Forget history (no interpolation across a discontinuous move). */
  teleport(obj: Object3D): void {
    const e = this.entries.get(obj);
    if (!e) return;
    e.prevP.copy(obj.position);
    e.prevQ.copy(obj.quaternion);
  }

  /** Forget history for every object (after respawn / stage reset). */
  teleportAll(): void {
    for (const obj of this.entries.keys()) this.teleport(obj);
  }

  clear(): void {
    this.entries.clear();
  }

  /** Before each fixed step: remember where things were. */
  beforeStep(): void {
    for (const e of this.entries.values()) {
      e.prevP.copy(e.obj.position);
      if (e.rotation) e.prevQ.copy(e.obj.quaternion);
    }
  }

  /** Before stage.frame()/render: move objects to their interpolated pose. */
  apply(alpha: number): void {
    const a = Math.max(0, Math.min(1, alpha));
    for (const e of this.entries.values()) {
      e.curP.copy(e.obj.position);
      e.obj.position.lerpVectors(e.prevP, e.curP, a);
      if (e.rotation) {
        e.curQ.copy(e.obj.quaternion);
        e.obj.quaternion.slerpQuaternions(e.prevQ, e.curQ, a);
      }
      e.applied = true;
    }
  }

  /** After render: put the simulated transforms back. */
  restore(): void {
    for (const e of this.entries.values()) {
      if (!e.applied) continue;
      e.obj.position.copy(e.curP);
      if (e.rotation) e.obj.quaternion.copy(e.curQ);
      e.applied = false;
    }
  }
}
